import styles from './AreaScope.module.scss';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { IJob, IPPEUsedItem, IUpdateJobPayload } from '~/utils/interface/job';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  InputLabel,
  TextField,
  Grid,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { PlusIcon, ArrowRightIcon, DeleteCircle } from '~/assets/icons';
import usePermission from '~/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { BaseRow, BaseSelect } from '~/components/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import { FormikErrors, useFormik } from 'formik';
import { cast, downloadCSV, handleApiError, notify } from '~/utils/helper';
import { handleUpdateJob } from '~/thunks/job/jobThunk';
import {
  MASTER_DATA_DEBRIS_DISPOSAL,
  MASTER_DATA_JOB_STATUSES,
  MASTER_DATA_PPE_USED,
  MASTER_DATA_TESTING,
} from '~/utils/constants/master';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { NumericFormat } from 'react-number-format';
import dayjs from 'dayjs';
import jobApi from '~/apis/job';

const cx = classNames.bind(styles);

type AreaScopeProps = {
  data: IJob | undefined;
  updateData: () => void;
};

const initialValues: IUpdateJobPayload = {
  job: {
    status: '',
    jobTypeId: '',
    statusId: '',
    testing: [],
  },
  jobAssigneeIds: [],
};

const AreaScope = (props: AreaScopeProps) => {
  const { data } = props;
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const [isEditing, setIsEditing] = useState(false); // Toggle for edit mode
  const { areaScopes } = useAppSelector((state) => state.areaScope);
  const navigate = useNavigate();
  const { testing, ppeUsed, debrisDisposal } = useAppSelector((state) => state.master);
  const { groupedMoisturePoints } = useAppSelector((state) => state.job);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(
          handleUpdateJob({
            id: Number(data?.id),
            params: { ...values, jobAssigneeIds: values?.jobAssigneeIds?.length ? values.jobAssigneeIds : '' },
          })
        ).unwrap();
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        setIsEditing(false);
        if (props.updateData) props.updateData();
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (props.updateData) props.updateData();
    setIsEditing(false); //
  };
  useEffect(() => {
    form.setFieldValue('job.status', cast(data?.status));
    form.setFieldValue('job.statusId', cast(data?.statusId));
    form.setFieldValue('jobAssigneeIds', cast(data?.assigneeIds));
    form.setFieldValue('job.billing', cast(data?.billing));
    form.setFieldValue('job.testing', cast(data?.testing));
    form.setFieldValue('job.ppeUsed', cast(data?.ppeUsed));
    form.setFieldValue('job.ppeUsedAmount', cast(data?.ppeUsedAmount));
    form.setFieldValue('job.jobPPEItems', cast(data?.jobPPEItems));
    form.setFieldValue('job.debrisDisposal', cast(data?.debrisDisposal));
    form.setFieldValue('job.debrisDisposalAmount', cast(data?.debrisDisposalAmount));

    form.setFieldValue('job.jobTypeId', cast(data?.jobTypeId));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditing]);
  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_JOB_STATUSES, MASTER_DATA_TESTING, MASTER_DATA_PPE_USED, MASTER_DATA_DEBRIS_DISPOSAL],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (data?.id) {
      dispatch(handleGetAreaScopeList({ jobId: data.id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, data?.id]);

  const handleOpenCreateAreaScope = () => {
    if (data?.id) {
      navigate(adminRouteAbsolute.areaScopeDetail.replace(':id', data.id).replace(':areaId', 'create'));
    }
  };

  const handleOpenCreateMoisturePoints = () => {
    if (data?.id) {
      navigate(
        `${adminRouteAbsolute.areaScopeMoistualPointsDetail.replace(':id', data?.id ?? '').replace(':index', 'create')}`
      );
    }
  };
  const addNewPPEItem = () => {
    form.setFieldValue('job.jobPPEItems', [
      ...(form.values.job.jobPPEItems ?? []),
      {
        name: undefined,
        quantity: undefined,
      },
    ]);
  };

  const handleOpenEditAreaScope = async (id: string) => {
    if (data?.id && id) {
      navigate(adminRouteAbsolute.areaScopeDetail.replace(':id', data.id).replace(':areaId', id));
    }
  };

  const handleOpenEditMoistualPoints = (key: string, area: string, items: any[]) => {
    navigate(
      adminRouteAbsolute.areaScopeMoistualPointsList.replace(':id', String(data?.id ?? '')).replace(':index', key),
      { state: { area, items } }
    );
  };

  const handleOpenEditPsychrometricReadings = async () => {
    if (data?.id) {
      navigate(adminRouteAbsolute.areaPsychrometricReadings.replace(':id', data.id));
    }
  };
  const handleDelete = (index: number) => {
    const updatedFields = [...(form.values.job.jobPPEItems ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('job.jobPPEItems', updatedFields);
  };
  const renderPPEItem = (_item: IPPEUsedItem, index: number) => {
    const fieldError =
      (form.errors.job?.jobPPEItems && (form.errors.job?.jobPPEItems[index] as FormikErrors<IPPEUsedItem>)) ||
      undefined;

    return (
      <BaseRow className='gap-[20px] mb-[20px]' key={index}>
        <Box className='w-full'>
          <InputLabel>{t('detail.ppeUsed')}</InputLabel>
          <BaseSelect
            options={ppeUsed}
            name={`job.jobPPEItems.${index}.name`}
            label={t('detail.ppeUsed')}
            value={form.values?.job.jobPPEItems && form.values?.job.jobPPEItems[index].name}
            onChange={form.handleChange}
            disabled={!isEditing}
            error={Boolean(fieldError?.name)}
            helperText={fieldError?.name}
          />
        </Box>

        <Box className='w-full'>
          <InputLabel>{t('detail.amount')}</InputLabel>

          <NumericFormat
            customInput={TextField}
            value={form.values?.job.jobPPEItems && form.values?.job.jobPPEItems[index].quantity}
            onValueChange={(val) => form.setFieldValue(`job.jobPPEItems.${index}.quantity`, val.floatValue)}
            thousandSeparator={true}
            disabled={!isEditing}
            error={Boolean(fieldError?.quantity)}
            helperText={fieldError?.quantity}
            decimalScale={2}
          />
        </Box>
        {isEditing && (
          <Box className='w-[20px]'>
            <DeleteCircle className='w-[20px] h-[20px] cursor-pointer mt-[27px]' onClick={() => handleDelete(index)} />
          </Box>
        )}
      </BaseRow>
    );
  };

  const handleExport = async () => {
    const now = dayjs().format('YYYY-MM-DD');
    const fileName = `${data?.name}_Moisture_points_${now}.csv`;
    const res = await jobApi.exportMoisturePoints({ jobId: data?.id ?? '' });
    downloadCSV(res.data, fileName);
  };

  return (
    <Box>
      <Box className={cx('header')}>{t('areaScope')}</Box>
      <Box component='form' className={cx('detail-container')}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>{t('detail.testing')}</InputLabel>
            <BaseSelect
              options={testing}
              name='job.testing'
              label={t('detail.testing')}
              value={form.values.job.testing}
              onChange={(e) => {
                form.setFieldValue('job.testing', e.target.value);
              }}
              disabled={!isEditing} // Disable field when not editing
              multiple
            />
          </Box>
        </BaseRow>
        <Divider className='mb-[10px]' />

        {form.values.job.jobPPEItems?.map((item: IPPEUsedItem, index: number) => renderPPEItem(item, index))}
        <Grid container spacing={3} className='mb-[24px]'>
          <Grid item xs={12} sm={4}>
            <Button onClick={addNewPPEItem} className='whitespace-nowrap' disabled={!isEditing}>
              {t('addNewPPEUsed')}
            </Button>
          </Grid>
        </Grid>
        <Divider className='mb-[10px]' />
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>{t('detail.debrisDisposal')}</InputLabel>
            <BaseSelect
              options={debrisDisposal}
              name='job.debrisDisposal'
              label={t('detail.debrisDisposal')}
              value={form.values.job.debrisDisposal}
              onChange={form.handleChange}
              disabled={!isEditing} // Disable field when not editing
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>{t('detail.amount')}</InputLabel>

            <NumericFormat
              customInput={TextField}
              value={cast(form.values.job.debrisDisposalAmount)}
              onValueChange={(val) => form.setFieldValue(`job.debrisDisposalAmount`, val.floatValue)}
              thousandSeparator={true}
              disabled={!isEditing}
              decimalScale={2}
            />
          </Box>
        </BaseRow>

        {permissions[PERMISSIONS.project_edit] && (
          <Box className='justify-end flex'>
            {isEditing ? (
              <>
                <Button className='text-[#758CA5] bg-[#F0F7FF] mr-[10px]' onClick={handleCancel}>
                  {t('cancel', { ns: 'admin/common' })}
                </Button>
                <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
                  {t('save', { ns: 'admin/common' })}
                </Button>
              </>
            ) : (
              <Button color='primary' variant='contained' onClick={handleEdit}>
                {t('edit', { ns: 'admin/common' })}
              </Button>
            )}
          </Box>
        )}
        {/* Edit and Save/Cancel Buttons */}
      </Box>
      <Divider />

      <Box className={cx('header')} sx={{ mt: '20px' }}>
        {t('areaRoom')}
      </Box>
      <List>
        {areaScopes.map((scope: any, index: any) => (
          <BaseRow className='gap-[20px] mb-[20px]' key={index}>
            <Box className='w-full' key={scope.id}>
              <ListItem>
                <ListItemText primary={scope.name} />
                <ListItemIcon>
                  {permissions[PERMISSIONS.projectAreaScope_edit] && (
                    <IconButton
                      className='hover:bg-[unset] ml-[30px]'
                      onClick={() => handleOpenEditAreaScope(scope.id)}
                    >
                      <ArrowRightIcon className='w-[13px] h-[13px] stroke-[#797D8A]' />
                    </IconButton>
                  )}
                </ListItemIcon>
              </ListItem>
              {scope !== areaScopes[areaScopes.length - 1] && <Divider />}
            </Box>
            <Box className='w-full'></Box>
          </BaseRow>
        ))}
      </List>
      <BaseRow className='gap-[20px] mb-[20px]'>
        <Box className='flex justify-end w-full'>
          {permissions[PERMISSIONS.projectAreaScope_create] && (
            <Button variant='text' startIcon={<PlusIcon />} onClick={handleOpenCreateAreaScope}>
              {t('addNewRoom')}
            </Button>
          )}
        </Box>
        <Box className='w-full'></Box>
      </BaseRow>
      <Divider />
      <Box className='flex justify-between items-center'>
        <Box className={cx('header-points')} sx={{ mt: '20px' }}>
          {`${t('areaMoisturePoints')}`}
        </Box>
        <Button
          className={'bg-[#10a479] mr-[10px] hover:bg-[#10a479]'}
          variant='contained'
          disableElevation
          onClick={handleExport}
        >
          {t('export')}
        </Button>
      </Box>

      <List>
        {groupedMoisturePoints.map(({ key, area, items }) => (
          <BaseRow className='gap-[20px] mb-[20px]' key={key}>
            <Box className='w-full'>
              <ListItem>
                <ListItemText primary={area ?? 'Unknown'} />
                <ListItemIcon>
                  <IconButton
                    className='hover:bg-[unset] ml-[30px]'
                    onClick={() => handleOpenEditMoistualPoints(key, area, items)}
                  >
                    <ArrowRightIcon className='w-[13px] h-[13px] stroke-[#797D8A]' />
                  </IconButton>
                </ListItemIcon>
              </ListItem>
            </Box>
            <Box className='w-full'></Box>
          </BaseRow>
        ))}
      </List>
      <BaseRow className='gap-[20px] mb-[20px]'>
        <Box className='flex justify-end w-full'>
          {permissions[PERMISSIONS.projectAreaScope_create] && (
            <Button variant='text' startIcon={<PlusIcon />} onClick={() => handleOpenCreateMoisturePoints()}>
              {t('addNewMoisturePoint')}
            </Button>
          )}
        </Box>
        <Box className='w-full'></Box>
      </BaseRow>

      <Divider />
      <Box className={cx('header')} sx={{ mt: '20px', pb: '20px' }}>
        {`${t('areaPsychrometricReadings')}`}
        <IconButton className='hover:bg-[unset] ml-[30px]' onClick={handleOpenEditPsychrometricReadings}>
          <ArrowRightIcon className='w-[13px] h-[13px] stroke-[#797D8A]' />
        </IconButton>
      </Box>
    </Box>
  );
};

export default memo(AreaScope);
